import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
  Tooltip,
  Paper,
  Divider
} from '@mui/material';
import {
  PhotoCamera,
  CheckCircle,
  CloudUpload,
  Delete,
  ArrowBack,
  Visibility,
  Add,
  AutoAwesome,
  Lightbulb,
  Close,
  Place
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import GeminiAITaskService from '../../../services/GeminiAITaskService';
import ApiService from '../../../services/api';
import { getAutoToken } from '../../../utils/authUtils';
import logger from '../../../utils/logger';
import { API_SERVER_URL } from '../../../config/api';
import GeotagDialog from '../../shared/GeotagDialog';
import GeotagService from '../../../services/GeotagService';
import { applyGeotagMetadata, normalizeGeotagMetadata } from '../../../utils/geotagging';

const readFilePreview = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (event) => resolve(event.target?.result || '');
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result || '');
  reader.onerror = () => reject(new Error('Failed to read image data'));
  reader.readAsDataURL(blob);
});

const fetchImageAsDataUrl = async (url) => {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }
  const blob = await response.blob();
  return blobToDataUrl(blob);
};

const resolvePhotoDataUrl = async (photo) => {
  if (!photo) return null;

  if (photo.file instanceof File) {
    return readFilePreview(photo.file);
  }

  const preview = typeof photo.preview === 'string' ? photo.preview.trim() : '';
  if (!preview) return null;

  if (preview.startsWith('data:image')) {
    return preview;
  }

  try {
    return await fetchImageAsDataUrl(preview);
  } catch (error) {
    logger.warn('[PhotoUploadPopup] Failed to resolve remote image preview for AI description:', error);
    return null;
  }
};

const HeaderContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
  color: 'white',
  padding: '40px 30px',
  textAlign: 'center',
  position: 'relative'
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 20px',
  backdropFilter: 'blur(10px)'
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  padding: '30px',
  maxHeight: '500px',
  overflowY: 'auto'
}));

const UploadArea = styled(Box)(({ theme }) => ({
  border: '2px dashed #4caf50',
  borderRadius: '12px',
  padding: '40px 20px',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: '#f8f9fa',
  '&:hover': {
    backgroundColor: '#e8f5e8',
    borderColor: '#2e7d32'
  },
  '&.dragover': {
    backgroundColor: '#e8f5e8',
    borderColor: '#2e7d32',
    transform: 'scale(1.02)'
  }
}));

const PhotoPreview = styled('img')(({ theme }) => ({
  width: '100%',
  height: '120px',
  objectFit: 'cover',
  borderRadius: '8px'
}));

const ActionButton = styled(Button)(({ theme, variant: buttonVariant }) => ({
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  ...(buttonVariant === 'primary' && {
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#388e3c'
    }
  }),
  ...(buttonVariant === 'secondary' && {
    backgroundColor: '#f5f5f5',
    color: '#333',
    '&:hover': {
      backgroundColor: '#e0e0e0'
    }
  })
}));

const PhotoUploadPopup = ({ task, onComplete, onBack, businessProfileData = {}, profileId }) => {
  const [photos, setPhotos] = useState([]);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [step, setStep] = useState('upload'); // upload, success, admin-review
  const [isDragging, setIsDragging] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [adminPhotos, setAdminPhotos] = useState([]);
  const [postingToGMB, setPostingToGMB] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [geotagDialogState, setGeotagDialogState] = useState({ open: false, photoId: null, defaults: null });
  const [geotagGenerating, setGeotagGenerating] = useState(false);
  const [autoGeotagging, setAutoGeotagging] = useState(false); // Track auto-geotagging progress
  const [geotagError, setGeotagError] = useState(''); // Track geotag validation errors

  const storageKey = profileId ? `visibeen_photos_${profileId}` : 'business_photos';

  // Map UI categories to valid GMB categories
  const toGmbCategory = (uiCategory) => {
    const c = String(uiCategory || '').trim().toLowerCase();
    if (c.includes('cover')) return 'COVER';
    if (c === 'profile' || c.includes('logo') || c.includes('dp')) return 'PROFILE';
    return 'ADDITIONAL';
  };

  // Helper function to extract accountId from stored session/GMB data
  const getAccountId = () => {
    try {
      // Try from businessProfileData first
      if (businessProfileData.accountId) return businessProfileData.accountId;
      if (businessProfileData.account_id) return businessProfileData.account_id;
      if (businessProfileData.account?.name) {
        const id = businessProfileData.account.name.split('/').pop();
        if (id) return id;
      }

      // Try from session storage
      const sessionStr = sessionStorage.getItem('session') || localStorage.getItem('session');
      if (sessionStr) {
        const session = JSON.parse(sessionStr);
        if (session.gmbAccounts && session.gmbAccounts.length > 0) {
          const firstAccount = session.gmbAccounts[0];
          // Account name format: "accounts/1234567890"
          if (firstAccount.name) {
            const accountId = firstAccount.name.split('/').pop();
            logger.log('📋 Extracted accountId from session gmbAccounts:', accountId);
            return accountId;
          }
          if (firstAccount.accountNumber) {
            return firstAccount.accountNumber;
          }
        }
      }

      // Try from storage keys
      return sessionStorage.getItem('gmb_account_id') ||
        localStorage.getItem('gmb_account_id') ||
        sessionStorage.getItem('google_account_id') ||
        localStorage.getItem('google_account_id');
    } catch (error) {
      logger.error('Error extracting accountId:', error);
      return null;
    }
  };

  // Fallback: fetch GMB accounts from Google and return first accountId
  const fetchGoogleAccountId = async (googleToken) => {
    try {
      if (!googleToken) return null;
      const res = await fetch('https://mybusinessaccountmanagement.googleapis.com/v1/accounts', {
        headers: {
          'Authorization': `Bearer ${googleToken}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      logger.log('🔎 GMB accounts fetch result:', data);
      const accName = data?.accounts?.[0]?.name || '';
      const accountId = accName.split('/').pop() || null;
      if (accountId) {
        try {
          sessionStorage.setItem('gmb_account_id', accountId);
          localStorage.setItem('gmb_account_id', accountId);
        } catch (_) { }
      }
      return accountId;
    } catch (err) {
      logger.warn('⚠️ Failed to fetch GMB accounts:', err);
      return null;
    }
  };

  // Check if admin uploaded photos for this task
  useEffect(() => {
    if (task && task.attachments) {
      try {
        const attachments = JSON.parse(task.attachments || '[]');
        if (Array.isArray(attachments) && attachments.length > 0) {
          setAdminPhotos(attachments);
          setStep('admin-review'); // Show admin photos for review
        }
      } catch (error) {
        logger.error('Failed to parse task attachments:', error);
      }
    }
  }, [task]);

  const photoCategories = [
    { name: 'Exterior', icon: '🏢', description: 'Building exterior, storefront, signage' },
    { name: 'Interior', icon: '🏠', description: 'Inside your business, workspace, decor' },
    { name: 'Products', icon: '📦', description: 'Your products, merchandise, inventory' },
    { name: 'Services', icon: '🔧', description: 'Services in action, work being done' },
    { name: 'Team', icon: '👥', description: 'Staff members, team photos' },
    { name: 'Equipment', icon: '⚙️', description: 'Tools, machinery, equipment used' }
  ];

  const deserializePhoto = (photo) => ({
    id: photo.id || `${Date.now()}-${Math.random()}`,
    name: photo.name || 'Business Photo',
    preview: photo.preview || photo.url || '',
    category: photo.category || 'Uncategorized',
    size: photo.size || 0,
    uploaded: photo.uploaded !== undefined ? photo.uploaded : true,
    uploadedAt: photo.uploadedAt,
    source: photo.source || 'gmb',
    file: null,
    geotag: photo.geotag ? normalizeGeotagMetadata(photo.geotag) : null
  });

  const createPhotoFromGMB = (photo) => {
    const previewUrl = photo.googleUrl || photo.thumbnailUrl || photo.sourceUrl || '';
    return {
      id: photo.mediaItemId || photo.name || `${Date.now()}-${Math.random()}`,
      name: photo.description || photo.filename || 'GMB Photo',
      preview: previewUrl,
      category: photo.category || 'Uncategorized',
      size: 0,
      uploaded: true,
      uploadedAt: photo.createTime || photo.updateTime,
      source: 'gmb',
      file: null,
      geotag: null
    };
  };

  const serializePhotos = (photoList) => photoList.map(photo => ({
    id: photo.id,
    name: photo.name,
    preview: photo.preview,
    category: photo.category,
    size: photo.size,
    uploaded: photo.uploaded,
    uploadedAt: photo.uploadedAt,
    source: photo.source,
    geotag: photo.geotag || null
  }));

  useEffect(() => {
    try {
      const savedPhotos = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(savedPhotos) && savedPhotos.length > 0) {
        setPhotos(savedPhotos.map(deserializePhoto));
        return;
      }
    } catch (error) {
      logger.warn('Failed to parse stored photos:', error);
    }

    const gmbPhotos = Array.isArray(businessProfileData.photos) ? businessProfileData.photos : [];
    if (gmbPhotos.length > 0) {
      const mapped = gmbPhotos
        .filter(photo => photo.googleUrl || photo.thumbnailUrl)
        .map(createPhotoFromGMB);
      if (mapped.length > 0) {
        setPhotos(mapped);
      }
    }
  }, [businessProfileData, storageKey]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    processFiles(files);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const files = Array.from(event.dataTransfer.files);
    processFiles(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  // Build default geotag metadata from business profile
  const getDefaultGeotagMetadata = () => {
    let lat = null;
    let lng = null;

    // Source 1: latlng object (GMB format)
    if (businessProfileData?.latlng?.latitude && businessProfileData?.latlng?.longitude) {
      lat = businessProfileData.latlng.latitude;
      lng = businessProfileData.latlng.longitude;
      console.log('✅ [PhotoUploadPopup] Found coords in latlng:', { lat, lng });
    }
    // Source 2: location.latlng
    else if (businessProfileData?.location?.latlng?.latitude && businessProfileData?.location?.latlng?.longitude) {
      lat = businessProfileData.location.latlng.latitude;
      lng = businessProfileData.location.latlng.longitude;
      console.log('✅ [PhotoUploadPopup] Found coords in location.latlng:', { lat, lng });
    }
    // Source 3: metadata.latlng
    else if (businessProfileData?.metadata?.latlng?.latitude && businessProfileData?.metadata?.latlng?.longitude) {
      lat = businessProfileData.metadata.latlng.latitude;
      lng = businessProfileData.metadata.latlng.longitude;
      console.log('✅ [PhotoUploadPopup] Found coords in metadata.latlng:', { lat, lng });
    }
    // Source 4: locationData.latlng (from GMB location data)
    else if (businessProfileData?.locationData?.latlng?.latitude && businessProfileData?.locationData?.latlng?.longitude) {
      lat = businessProfileData.locationData.latlng.latitude;
      lng = businessProfileData.locationData.latlng.longitude;
      console.log('✅ [PhotoUploadPopup] Found coords in locationData.latlng:', { lat, lng });
    }
    // Source 5: Direct lat/lng properties
    else if (businessProfileData?.latitude != null && businessProfileData?.longitude != null) {
      lat = businessProfileData.latitude;
      lng = businessProfileData.longitude;
      console.log('✅ [PhotoUploadPopup] Found coords in direct props:', { lat, lng });
    }
    // Source 6: lat/lng shorthand
    else if (businessProfileData?.lat != null && businessProfileData?.lng != null) {
      lat = businessProfileData.lat;
      lng = businessProfileData.lng;
      console.log('✅ [PhotoUploadPopup] Found coords in lat/lng:', { lat, lng });
    }
    // Source 7: storefrontAddress
    else if (businessProfileData?.storefrontAddress?.latitude && businessProfileData?.storefrontAddress?.longitude) {
      lat = businessProfileData.storefrontAddress.latitude;
      lng = businessProfileData.storefrontAddress.longitude;
      console.log('✅ [PhotoUploadPopup] Found coords in storefrontAddress:', { lat, lng });
    }
    // Source 8: geometry.location (Google Places format)
    else if (businessProfileData?.geometry?.location) {
      const geoLoc = businessProfileData.geometry.location;
      lat = typeof geoLoc.lat === 'function' ? geoLoc.lat() : geoLoc.lat;
      lng = typeof geoLoc.lng === 'function' ? geoLoc.lng() : geoLoc.lng;
      if (lat != null && lng != null) {
        console.log('✅ [PhotoUploadPopup] Found coords in geometry.location:', { lat, lng });
      }
    }

    if (lat == null || lng == null) {
      console.warn('⚠️ [PhotoUploadPopup] No coordinates found in businessProfileData:', Object.keys(businessProfileData || {}));
      return null;
    }

    const keywords = businessProfileData?.selectedKeywords || [];
    const businessName = businessProfileData?.title || businessProfileData?.name || '';
    const category = businessProfileData?.category || businessProfileData?.categories?.primaryCategory?.displayName || '';
    const city = businessProfileData?.storefrontAddress?.locality || businessProfileData?.city || '';

    // Build a default description
    const descParts = [businessName, category, city].filter(Boolean);
    const description = descParts.length > 0
      ? `${descParts.join(' - ')}`
      : 'Business photo';

    return normalizeGeotagMetadata({
      latitude: lat,
      longitude: lng,
      keywords: keywords.length > 0 ? keywords : [businessName, category].filter(Boolean),
      description
    });
  };

  // Auto-apply geotag to a photo
  const autoApplyGeotagToPhoto = async (photo, defaultMetadata) => {
    if (!photo.file || !defaultMetadata) return photo;

    try {
      const result = await applyGeotagMetadata(photo.file, defaultMetadata);
      if (result.applied && result.file) {
        const updatedPreview = await readFilePreview(result.file);
        logger.log(`[PhotoUploadPopup] Auto-applied geotag to ${photo.name}`);
        return {
          ...photo,
          file: result.file,
          preview: updatedPreview,
          size: result.file.size,
          geotag: defaultMetadata
        };
      }
    } catch (error) {
      logger.warn(`[PhotoUploadPopup] Failed to auto-apply geotag to ${photo.name}:`, error);
    }
    return photo;
  };

  const processFiles = async (files) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    setAutoGeotagging(true);
    setGeotagError(''); // Clear any previous errors

    const defaultMetadata = getDefaultGeotagMetadata();

    for (const file of imageFiles) {
      try {
        const preview = await readFilePreview(file);
        let newPhoto = {
          id: Date.now() + Math.random(),
          file,
          preview,
          name: file.name,
          size: file.size,
          category: 'Uncategorized',
          uploaded: false,
          geotag: null
        };

        // Auto-apply geotag if we have default metadata
        if (defaultMetadata) {
          newPhoto = await autoApplyGeotagToPhoto(newPhoto, defaultMetadata);
        }

        setPhotos(prev => [...prev, newPhoto]);
      } catch (error) {
        logger.error(`[PhotoUploadPopup] Failed to process file ${file.name}:`, error);
      }
    }

    setAutoGeotagging(false);

    // Show warning if no default geotag available
    if (!defaultMetadata) {
      setGeotagError('⚠️ Business profile coordinates not found. Please add geotag manually to each photo.');
    }
  };

  const handlePhotoDelete = (photoId) => {
    setPhotos((prev) => prev.filter((photo) => photo.id !== photoId));
    if (geotagDialogState.photoId === photoId) {
      setGeotagDialogState({ open: false, photoId: null, defaults: null });
    }
  };

  const handleCategoryChange = (photoId, category) => {
    setPhotos(photos.map(photo =>
      photo.id === photoId ? { ...photo, category } : photo
    ));
  };

  const openGeotagDialogForPhoto = (photo) => {
    // Extract coordinates from multiple sources (matching getDefaultGeotagMetadata logic)
    let businessCoordinates = null;

    if (businessProfileData?.latlng?.latitude && businessProfileData?.latlng?.longitude) {
      businessCoordinates = businessProfileData.latlng;
    } else if (businessProfileData?.location?.latlng?.latitude && businessProfileData?.location?.latlng?.longitude) {
      businessCoordinates = businessProfileData.location.latlng;
    } else if (businessProfileData?.locationData?.latlng?.latitude && businessProfileData?.locationData?.latlng?.longitude) {
      businessCoordinates = businessProfileData.locationData.latlng;
    } else if (businessProfileData?.metadata?.latlng?.latitude && businessProfileData?.metadata?.latlng?.longitude) {
      businessCoordinates = businessProfileData.metadata.latlng;
    } else if (businessProfileData?.latitude != null && businessProfileData?.longitude != null) {
      businessCoordinates = { latitude: businessProfileData.latitude, longitude: businessProfileData.longitude };
    } else if (businessProfileData?.lat != null && businessProfileData?.lng != null) {
      businessCoordinates = { latitude: businessProfileData.lat, longitude: businessProfileData.lng };
    } else if (businessProfileData?.storefrontAddress?.latitude && businessProfileData?.storefrontAddress?.longitude) {
      businessCoordinates = { latitude: businessProfileData.storefrontAddress.latitude, longitude: businessProfileData.storefrontAddress.longitude };
    }

    const defaults = GeotagService.buildDefaultMetadata({
      businessProfileData,
      businessCoordinates,
      existingMetadata: photo.geotag,
      businessKeywords: businessProfileData?.selectedKeywords,
      category: photo.category
    });

    setGeotagDialogState({
      open: true,
      photoId: photo.id,
      defaults,
      initial: photo.geotag || null
    });
  };

  const handleApplyGeotag = async (metadata) => {
    const targetId = geotagDialogState.photoId;
    if (!targetId) {
      setGeotagDialogState({ open: false, photoId: null, defaults: null });
      return;
    }

    const normalized = normalizeGeotagMetadata(metadata);
    const photo = photos.find((item) => item.id === targetId);

    if (!photo) {
      setGeotagDialogState({ open: false, photoId: null, defaults: null });
      return;
    }

    let updatedFile = photo.file;
    let updatedPreview = photo.preview;
    let updatedSize = photo.size;

    if (photo.file) {
      const result = await applyGeotagMetadata(photo.file, normalized);
      if (result.applied && result.file) {
        updatedFile = result.file;
        updatedSize = result.file.size;
        try {
          updatedPreview = await readFilePreview(result.file);
        } catch (previewError) {
          logger.warn('[PhotoUploadPopup] Failed to refresh preview after geotagging:', previewError);
        }
        logger.log(`[PhotoUploadPopup] Geotag metadata embedded for ${photo.name}`);
      } else if (result.reason) {
        logger.warn(`[PhotoUploadPopup] Geotag embedding skipped for ${photo.name}: ${result.reason}`);
        alert(`Could not embed geotag data into ${photo.name}: ${result.reason}`);
      } else if (result.error) {
        logger.error('[PhotoUploadPopup] Geotag embedding failed:', result.error);
        alert(`Failed to embed geotag data into ${photo.name}. Please try again.`);
      }
    } else {
      logger.warn('[PhotoUploadPopup] No local file available for geotagging. Metadata will be saved but not embedded.');
    }

    setPhotos((prev) => prev.map((item) => (
      item.id === targetId
        ? {
          ...item,
          geotag: normalized,
          file: updatedFile,
          preview: updatedPreview,
          size: updatedSize
        }
        : item
    )));

    setGeotagDialogState({ open: false, photoId: null, defaults: null });
  };

  const handleGenerateGeotagDescription = async () => {
    const targetId = geotagDialogState.photoId;
    if (!targetId) return null;
    const photo = photos.find((item) => item.id === targetId);
    if (!photo) return null;

    setGeotagGenerating(true);
    try {
      const imageBase64 = await resolvePhotoDataUrl(photo);
      const description = await GeotagService.generateDescription({
        businessProfileData,
        businessCoordinates: businessProfileData?.latlng,
        businessKeywords: businessProfileData?.selectedKeywords,
        businessName: businessProfileData?.title || businessProfileData?.name,
        address: businessProfileData?.storefrontAddress?.locality,
        category: photo.category || businessProfileData?.category,
        existingMetadata: photo.geotag,
        imageBase64
      });
      return description;
    } finally {
      setGeotagGenerating(false);
    }
  };

  const handleUploadPhotos = async () => {
    const uploadQueue = photos.filter(photo => photo.file && !photo.uploaded);
    if (uploadQueue.length === 0) {
      alert('No new photos to upload. Please select photos first.');
      return;
    }

    // MANDATORY GEOTAG VALIDATION: Check if all photos have geotag
    const photosWithoutGeotag = uploadQueue.filter(photo =>
      !photo.geotag || photo.geotag.latitude == null || photo.geotag.longitude == null
    );

    if (photosWithoutGeotag.length > 0) {
      const names = photosWithoutGeotag.map(p => p.name).join(', ');
      setGeotagError(`❌ GEOTAG REQUIRED: The following photos need geotag before upload: ${names}`);
      alert(`Cannot upload: ${photosWithoutGeotag.length} photo(s) are missing geotag.\n\nPlease click the "Geotag" button on each photo to add location data.\n\nPhotos: ${names}`);
      return;
    }

    setGeotagError(''); // Clear error if validation passes

    setUploading(true);
    setUploadProgress(0);
    setUploadErrors([]);

    try {
      // Get GMB credentials
      // IMPORTANT: Get Google OAuth token, not application JWT token
      const googleToken = sessionStorage.getItem('google_access_token') ||
        localStorage.getItem('google_access_token') ||
        sessionStorage.getItem('googleAccessToken') ||
        localStorage.getItem('googleAccessToken');

      // Get accountId using helper function; if not present, fetch from Google
      let rawAccountId = getAccountId();
      if (!rawAccountId) {
        logger.log('ℹ️ accountId not found locally, fetching from Google...');
        rawAccountId = await fetchGoogleAccountId(googleToken);
      }

      const accountId = rawAccountId?.replace('accounts/', '');

      // Persist for subsequent calls if found
      try {
        if (accountId) {
          sessionStorage.setItem('gmb_account_id', accountId);
          localStorage.setItem('gmb_account_id', accountId);
        }
      } catch (_) { }

      const rawLocationId = profileId ||
        businessProfileData.locationId ||
        businessProfileData.location_id ||
        sessionStorage.getItem('gmb_location_id') ||
        (JSON.parse(sessionStorage.getItem('session') || '{}')?.gmbPrimaryLocation?.name?.split('/').pop());

      const locationId = rawLocationId?.replace('locations/', '');

      logger.log('📋 GMB Credentials Check:', {
        hasGoogleToken: !!googleToken,
        tokenPreview: googleToken ? `${googleToken.substring(0, 20)}...` : 'NO TOKEN',
        accountId,
        locationId,
        profileId,
        businessProfileData: {
          accountId: businessProfileData.accountId,
          account_id: businessProfileData.account_id,
          locationId: businessProfileData.locationId,
          location_id: businessProfileData.location_id
        }
      });

      if (!accountId) {
        throw new Error('Missing GMB account information. Please reconnect Google in Business Profile (Dashboard > Connect Google).');
      }

      if (!googleToken) {
        throw new Error('Google authentication required. Please sign in to Google.');
      }

      if (!locationId) {
        throw new Error('Missing GMB location information. Please open Business Profile once to load locations.');
      }

      logger.log('📤 Starting GMB photo upload via backend API...', {
        accountId,
        locationId,
        photoCount: uploadQueue.length
      });

      const updatedPhotos = [...photos];
      const errors = [];
      let successCount = 0;

      for (let i = 0; i < uploadQueue.length; i++) {
        const queuedPhoto = uploadQueue[i];

        try {
          logger.log(`📸 Uploading photo ${i + 1}/${uploadQueue.length}: ${queuedPhoto.name}`);
          logger.log('📋 Upload details:', {
            accountId,
            locationId,
            category: queuedPhoto.category || 'ADDITIONAL',
            fileName: queuedPhoto.file.name,
            fileSize: queuedPhoto.file.size,
            fileType: queuedPhoto.file.type,
            hasGeotag: !!queuedPhoto.geotag
          });

          // Create FormData for file upload
          const formData = new FormData();

          if (queuedPhoto.geotag) {
            const metadata = normalizeGeotagMetadata(queuedPhoto.geotag);
            if (metadata.latitude != null && metadata.longitude != null) {
              formData.append('geotag', JSON.stringify(metadata));
            }
          }

          formData.append('photo', queuedPhoto.file);
          formData.append('accountId', accountId);
          formData.append('locationId', locationId);
          formData.append('category', toGmbCategory(queuedPhoto.category));

          logger.log('📤 Sending request to /gmb/upload-photo...');

          // Upload to GMB via backend API
          const response = await ApiService.request('/gmb/upload-photo', {
            method: 'POST',
            body: formData,
            headers: {
              'Authorization': `Bearer ${googleToken}` // Send Google OAuth token
            }
          });

          logger.log('📥 Response received:', response);

          // Normalize backend response
          const uploadOk = (response && (response.success === true || response.code === 200 || response.status === 200) && !response.error);
          const uploadResult = response?.data?.mediaItem || response?.mediaItem || null;
          if (!uploadOk && !uploadResult) {
            throw new Error(response?.message || 'Upload failed');
          }
          logger.log('✅ Photo uploaded successfully:', uploadResult);

          const now = new Date().toISOString();
          const updatedPhoto = {
            ...queuedPhoto,
            uploaded: true,
            uploadedAt: now,
            size: queuedPhoto.file.size,
            source: 'gmb',
            mediaItemId: uploadResult.name,
            googleUrl: uploadResult.googleUrl || uploadResult.sourceUrl
          };

          const indexInState = updatedPhotos.findIndex(photo => photo.id === queuedPhoto.id);
          if (indexInState >= 0) {
            updatedPhotos[indexInState] = updatedPhoto;
          }

          successCount++;
        } catch (photoError) {
          logger.error(`❌ Failed to upload photo ${queuedPhoto.name}:`, photoError);
          logger.error('❌ Error details:', {
            message: photoError.message,
            status: photoError.status,
            body: photoError.body,
            stack: photoError.stack
          });
          errors.push({
            photoName: queuedPhoto.name,
            error: photoError.message || 'Upload failed'
          });
        }

        setUploadProgress(((i + 1) / uploadQueue.length) * 100);
      }

      setPhotos(updatedPhotos);
      localStorage.setItem(storageKey, JSON.stringify(serializePhotos(updatedPhotos)));

      if (errors.length > 0) {
        setUploadErrors(errors);
        logger.warn(`⚠️ Upload completed with ${errors.length} errors`);
      }

      if (successCount > 0) {
        logger.log(`✅ Successfully uploaded ${successCount}/${uploadQueue.length} photos to GMB`);
        setStep('success');
      } else {
        throw new Error('All photo uploads failed. Please check your connection and try again.');
      }
    } catch (error) {
      logger.error('❌ Upload error:', error);
      logger.error('❌ Full error details:', {
        message: error.message,
        status: error.status,
        body: error.body,
        stack: error.stack
      });
      alert(`Error uploading photos: ${error.message}\n\nPlease check:\n1. You are signed in to Google\n2. You have permission to manage this GMB profile\n3. Your internet connection is stable\n\nError details: ${JSON.stringify(error.body || error.message)}`);
    } finally {
      setUploading(false);
    }
  };

  // 🤖 AI Photo Strategy
  const handleGeneratePhotoStrategy = async () => {
    try {
      setLoadingAI(true);
      setShowAISuggestions(true);

      logger.log('🤖 [AI Photo] Generating strategy...');

      const context = {
        businessInfo: {
          name: businessProfileData.name || 'Business',
          category: businessProfileData.category || 'business'
        },
        currentPhotos: uploadedPhotos
      };

      const suggestions = await GeminiAITaskService.generatePhotoSuggestions(task, context);
      setAiSuggestions(suggestions);
      logger.log('✅ [AI Photo] Strategy generated successfully');

    } catch (error) {
      logger.error('❌ Error generating photo strategy:', error);
      setAiSuggestions({
        type: 'photo_upload',
        title: '📸 Photo Strategy',
        tips: [
          'Upload at least 25 photos for maximum visibility',
          'Include exterior, interior, team, and product photos',
          'Use natural lighting when possible',
          'Update photos every 2-3 months'
        ]
      });
    } finally {
      setLoadingAI(false);
    }
  };

  const handleCompleteTask = () => {
    const localUploads = photos.filter(photo => photo.source === 'local');
    const gmbPhotos = photos.filter(photo => photo.source === 'gmb');

    onComplete({
      type: 'photos',
      completed: true,
      gmbSynced: gmbPhotos.length > 0,
      photosUploaded: localUploads.length,
      existingPhotos: gmbPhotos.length,
      totalPhotos: photos.length,
      categories: [...new Set(photos.map(p => p.category))],
      totalSize: localUploads.reduce((sum, photo) => sum + (photo.size || 0), 0),
      completedAt: new Date().toISOString(),
      // Include photo previews for completed task display
      photoUrls: photos.slice(0, 6).map(p => ({
        url: p.preview || p.url || p.sourceUrl,
        category: p.category,
        name: p.name
      }))
    });
  };

  // Handle posting admin photos to GMB
  const handlePostToGMB = async () => {
    try {
      setPostingToGMB(true);
      setUploadErrors([]);
      logger.log('📤 Posting admin photos to GMB via backend API...');

      // Get GMB credentials
      // IMPORTANT: Get Google OAuth token, not application JWT token
      const googleToken = sessionStorage.getItem('google_access_token') ||
        localStorage.getItem('google_access_token') ||
        sessionStorage.getItem('googleAccessToken') ||
        localStorage.getItem('googleAccessToken');

      // Get accountId using helper function; if not present, fetch from Google and persist
      let accountId = getAccountId();
      if (!accountId) {
        logger.log('ℹ️ accountId not found for admin photos, fetching from Google...');
        accountId = await fetchGoogleAccountId(googleToken);
      }

      const locationId = profileId ||
        businessProfileData.locationId ||
        businessProfileData.location_id ||
        sessionStorage.getItem('gmb_location_id') ||
        (JSON.parse(sessionStorage.getItem('session') || '{}')?.gmbPrimaryLocation?.name?.split('/').pop());

      logger.log('📋 GMB Credentials for Admin Photos:', {
        hasGoogleToken: !!googleToken,
        accountId,
        locationId
      });

      if (!googleToken) {
        throw new Error('Google authentication required. Please sign in to Google.');
      }

      if (!accountId || !locationId) {
        throw new Error('Missing GMB account or location information.');
      }

      const errors = [];
      let successCount = 0;

      // Upload each admin photo to GMB
      for (let i = 0; i < adminPhotos.length; i++) {
        const photoPath = adminPhotos[i];

        try {
          logger.log(`📸 Uploading admin photo ${i + 1}/${adminPhotos.length}`);

          // Fetch the photo from backend
          const photoUrl = `${API_SERVER_URL}${photoPath}`;
          const response = await fetch(photoUrl);

          if (!response.ok) {
            throw new Error('Failed to fetch photo from server');
          }

          const blob = await response.blob();
          const fileName = photoPath.split('/').pop();
          let file = new File([blob], fileName, { type: blob.type });

          // Auto-apply geotag if metadata available
          const defaultMetadata = getDefaultGeotagMetadata();
          let geotagToUpload = null;

          if (defaultMetadata) {
            try {
              logger.log(`📍 Applying auto-geotag to admin photo: ${fileName}`);
              const result = await applyGeotagMetadata(file, defaultMetadata);
              if (result.applied && result.file) {
                file = result.file;
                geotagToUpload = defaultMetadata;
                logger.log('✅ Geotag applied successfully to admin photo');
              }
            } catch (geotagErr) {
              logger.warn('⚠️ Failed to apply geotag to admin photo, uploading anyway:', geotagErr);
            }
          }

          // Create FormData for file upload
          const formData = new FormData();
          formData.append('photo', file);
          formData.append('accountId', accountId);
          formData.append('locationId', locationId);
          formData.append('category', 'ADDITIONAL');

          if (geotagToUpload) {
            formData.append('geotag', JSON.stringify(geotagToUpload));
          }

          // Upload to GMB via backend API
          const uploadResponse = await ApiService.request('/gmb/upload-photo', {
            method: 'POST',
            body: formData,
            headers: {
              'Authorization': `Bearer ${googleToken}` // Send Google OAuth token
            }
          });

          // Normalize backend response (support { success:true } or { code:200 } shapes)
          const adminUploadOk = (uploadResponse && (uploadResponse.success === true || uploadResponse.code === 200 || uploadResponse.status === 200) && !uploadResponse.error);
          const adminMediaItem = uploadResponse?.data?.mediaItem || uploadResponse?.mediaItem || null;
          if (!adminUploadOk && !adminMediaItem) {
            throw new Error(uploadResponse?.message || 'Upload failed');
          }

          logger.log('✅ Admin photo uploaded successfully:', adminMediaItem);
          successCount++;
        } catch (photoError) {
          logger.error(`❌ Failed to upload admin photo ${i + 1}:`, photoError);
          errors.push({
            photoName: `Photo ${i + 1}`,
            error: photoError.message || 'Upload failed'
          });
        }
      }

      if (errors.length > 0) {
        setUploadErrors(errors);
      }

      if (successCount > 0) {
        logger.log(`✅ Successfully posted ${successCount}/${adminPhotos.length} photos to GMB`);

        // Complete the task
        onComplete({
          type: 'photos',
          completed: true,
          photosUploaded: successCount,
          photosFailed: errors.length,
          action: 'posted_to_gmb',
          completedAt: new Date().toISOString(),
          // Include photo previews for completed task display
          photoUrls: adminPhotos.slice(0, 6).map(p => ({
            url: p.url || p.preview || p.sourceUrl,
            category: p.category,
            name: p.name
          }))
        });
      } else {
        throw new Error('All photo uploads failed. Please try again.');
      }
    } catch (error) {
      logger.error('❌ Error posting to GMB:', error);
      alert(`Failed to post photos to GMB: ${error.message}\n\nPlease check your Google account connection and try again.`);
    } finally {
      setPostingToGMB(false);
    }
  };

  // Handle rejecting admin photos
  const handleRejectPhotos = () => {
    logger.log('❌ Client rejected admin photos');

    // Complete task with rejection
    onComplete({
      type: 'photos',
      completed: true,
      action: 'rejected',
      photosRejected: adminPhotos.length,
      completedAt: new Date().toISOString()
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderPhotoUpload = () => (
    <ContentContainer>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
        Upload Business Photos
      </Typography>

      {/* AI Photo Strategy */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={loadingAI ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <AutoAwesome />}
          onClick={handleGeneratePhotoStrategy}
          disabled={loadingAI || showAISuggestions}
          sx={{
            backgroundColor: '#9c27b0',
            '&:hover': { backgroundColor: '#7b1fa2' },
            mb: 2,
            width: '100%'
          }}
        >
          {loadingAI ? 'Generating...' : showAISuggestions ? 'AI Strategy Generated' : '✨ Get AI Photo Strategy'}
        </Button>

        {showAISuggestions && aiSuggestions && (
          <Paper elevation={3} sx={{ p: 3, backgroundColor: '#f8f5ff', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#9c27b0', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesome /> {aiSuggestions.title}
            </Typography>

            {/* Photo Categories */}
            {aiSuggestions.actionItems && aiSuggestions.actionItems.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Recommended Photo Categories:
                </Typography>
                {aiSuggestions.actionItems.map((item, idx) => (
                  <Box key={idx} sx={{ mb: 2, p: 2, backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                        {item.category}
                      </Typography>
                      <Chip label={item.priority} size="small" color={item.priority === 'High' ? 'error' : 'default'} />
                      <Chip label={`Min: ${item.minPhotos} photos`} size="small" variant="outlined" />
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '13px', mb: 0.5 }}>
                      {item.description}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>
                      💡 {item.tips}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Tips */}
            {aiSuggestions.tips && aiSuggestions.tips.length > 0 && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff9e6', borderRadius: '8px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Lightbulb sx={{ fontSize: 18, color: '#ff9800' }} />
                  Best Practices:
                </Typography>
                {aiSuggestions.tips.map((tip, idx) => (
                  <Typography key={idx} variant="body2" sx={{ color: '#666', fontSize: '13px', mb: 0.5 }}>
                    💡 {tip}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Stats */}
            {aiSuggestions.stats && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  📊 Progress:
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={aiSuggestions.stats.completion || 0}
                  sx={{ mb: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
                  Current Photos: <strong>{aiSuggestions.stats.currentPhotos || 0}</strong> / Recommended: <strong>{aiSuggestions.stats.recommended || 25}</strong>
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Photo Categories Guide */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, color: '#666' }}>
          Recommended Photo Types:
        </Typography>
        <Grid container spacing={2}>
          {photoCategories.map((category, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: '20px', mr: 1 }}>
                      {category.icon}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {category.name}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {category.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Upload Area */}
      <UploadArea
        className={dragOver ? 'dragover' : ''}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <CloudUpload sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
          Drop photos here or click to browse
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Supported formats: JPG, PNG, GIF (Max 10MB each)
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          sx={{ borderColor: '#4caf50', color: '#4caf50' }}
        >
          Select Photos
        </Button>
      </UploadArea>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Photo Previews */}
      {photos.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Selected Photos ({photos.length})
          </Typography>

          <Grid container spacing={2}>
            {photos.map((photo) => (
              <Grid item xs={12} sm={6} md={4} key={photo.id}>
                <Card>
                  <Box sx={{ position: 'relative' }}>
                    <PhotoPreview src={photo.preview} alt={photo.name} />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                      }}
                      size="small"
                      onClick={() => handlePhotoDelete(photo.id)}
                    >
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {photo.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                      {formatFileSize(photo.size)}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {photoCategories.map((cat) => (
                        <Chip
                          key={cat.name}
                          label={cat.name}
                          size="small"
                          variant={photo.category === cat.name ? 'filled' : 'outlined'}
                          onClick={() => handleCategoryChange(photo.id, cat.name)}
                          sx={{
                            fontSize: '10px',
                            height: '20px',
                            mb: 0.5,
                            ...(photo.category === cat.name && {
                              backgroundColor: '#4caf50',
                              color: 'white'
                            })
                          }}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant={photo.geotag?.latitude != null ? 'outlined' : 'contained'}
                      size="small"
                      color={photo.geotag?.latitude != null ? 'success' : 'warning'}
                      startIcon={<Place sx={{ fontSize: 16 }} />}
                      onClick={() => openGeotagDialogForPhoto(photo)}
                      sx={{
                        ...(photo.geotag?.latitude == null && {
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.4)' },
                            '70%': { boxShadow: '0 0 0 10px rgba(255, 152, 0, 0)' },
                            '100%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0)' },
                          },
                        })
                      }}
                    >
                      {photo.geotag?.latitude != null ? '✓ Geotag' : '⚠️ Add Geotag (Required)'}
                    </Button>
                    {photo.geotag?.latitude != null && photo.geotag?.longitude != null && (
                      <Chip
                        label={`📍 ${photo.geotag.latitude.toFixed(4)}, ${photo.geotag.longitude.toFixed(4)}`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Auto-geotagging Progress */}
          {autoGeotagging && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={20} />
                <Typography>Auto-applying geotag to photos...</Typography>
              </Stack>
            </Alert>
          )}

          {/* Geotag Validation Warning */}
          {geotagError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {geotagError}
            </Alert>
          )}

          {/* Summary of geotag status */}
          {photos.length > 0 && !autoGeotagging && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                📍 Geotag Status (Required for all photos)
              </Typography>
              <Stack direction="row" spacing={2}>
                <Chip
                  label={`✓ ${photos.filter(p => p.geotag?.latitude != null).length} with geotag`}
                  size="small"
                  color="success"
                />
                {photos.filter(p => !p.geotag?.latitude).length > 0 && (
                  <Chip
                    label={`⚠️ ${photos.filter(p => !p.geotag?.latitude).length} missing geotag`}
                    size="small"
                    color="warning"
                  />
                )}
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Upload Progress */}
      {uploading && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Uploading photos to Google My Business... {Math.round(uploadProgress)}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#666' }}>
            Please wait, this may take a moment...
          </Typography>
        </Box>
      )}

      {/* Upload Errors */}
      {uploadErrors.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Some photos failed to upload:
          </Typography>
          {uploadErrors.map((error, idx) => (
            <Typography key={idx} variant="caption" sx={{ display: 'block' }}>
              • {error.photoName}: {error.error}
            </Typography>
          ))}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 4, justifyContent: 'center' }}>
        <ActionButton variant="secondary" onClick={onBack} startIcon={<ArrowBack />}>
          Back
        </ActionButton>
        <ActionButton
          variant="primary"
          onClick={handleUploadPhotos}
          disabled={photos.length === 0 || uploading || autoGeotagging || photos.some(p => !p.geotag?.latitude)}
          startIcon={uploading ? <CloudUpload /> : <PhotoCamera />}
          sx={{
            ...(photos.some(p => !p.geotag?.latitude) && photos.length > 0 && {
              opacity: 0.6,
              cursor: 'not-allowed'
            })
          }}
          title={photos.some(p => !p.geotag?.latitude) ? 'All photos must have geotag before upload' : ''}
        >
          {uploading ? 'Uploading...' :
            photos.some(p => !p.geotag?.latitude) && photos.length > 0 ?
              `⚠️ Add Geotag to ${photos.filter(p => !p.geotag?.latitude).length} Photo(s)` :
              `Upload ${photos.length} Photos`}
        </ActionButton>
      </Stack>
    </ContentContainer>
  );

  const renderSuccess = () => (
    <ContentContainer>
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 3 }} />
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
          Photos Uploaded Successfully!
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
          Your business photos have been updated and will help attract more customers.
        </Typography>

        <Card sx={{ mb: 4, textAlign: 'left' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Upload Summary
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="textSecondary">Photos Uploaded:</Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#4caf50' }}>
                  {photos.filter(photo => photo.source === 'local').length}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Total Size:</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatFileSize(photos.filter(photo => photo.source === 'local').reduce((sum, photo) => sum + (photo.size || 0), 0))}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Existing GMB Photos:</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                  {photos.filter(photo => photo.source === 'gmb').length}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Categories:</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {[...new Set(photos.map(p => p.category))].map((category, index) => (
                    <Chip
                      key={index}
                      label={category}
                      size="small"
                      sx={{ backgroundColor: '#e8f5e8', color: '#2e7d32' }}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <ActionButton
          variant="primary"
          startIcon={<CheckCircle />}
          onClick={handleCompleteTask}
        >
          Complete Task
        </ActionButton>
      </Box>
    </ContentContainer>
  );

  // Render admin photo review screen
  const renderAdminPhotoReview = () => (
    <ContentContainer>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          📸 Admin has uploaded photos for you!
        </Typography>
        <Typography variant="body2">
          Review the photos below and choose to post them to your Google My Business profile or request different photos.
        </Typography>
      </Alert>

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
        Photos from Admin ({adminPhotos.length} files)
      </Typography>

      {/* Geotag Status Indicator - Premium Design */}
      {(() => {
        const defaultMetadata = getDefaultGeotagMetadata();
        const hasCoords = defaultMetadata?.latitude != null;
        return (
          <Box sx={{
            mb: 3,
            p: '1px',
            borderRadius: 3,
            background: hasCoords
              ? 'linear-gradient(135deg, #10B981 0%, #3B82F6 100%)'
              : 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <Box sx={{
              p: 2,
              borderRadius: '11px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                background: hasCoords
                  ? 'linear-gradient(135deg, #D1FAE5 0%, #EFF6FF 100%)'
                  : 'linear-gradient(135deg, #FEF3C7 0%, #FEF2F2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                {hasCoords ? '📍' : '❓'}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1F2937', fontSize: '14px' }}>
                  {hasCoords ? 'Auto-Geotagging Active for Task' : 'Geotagging UnAvailable'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', lineHeight: 1.2 }}>
                  {hasCoords
                    ? `We'll automatically embed coordinates and business metadata into these ${adminPhotos.length} photos during upload.`
                    : 'Business coordinates not found. Photos will be uploaded without SEO metadata.'}
                </Typography>
              </Box>
              {hasCoords && (
                <Chip label="SEO Optimized" size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 700, height: 20, fontSize: '10px' }} />
              )}
            </Box>
          </Box>
        );
      })()}

      {/* Photo Gallery */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {adminPhotos.map((photoPath, index) => {
          const defaultMetadata = getDefaultGeotagMetadata();
          const hasCoords = defaultMetadata?.latitude != null;

          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ height: '100%', position: 'relative' }}>
                {hasCoords && (
                  <Chip
                    label="📍 Auto-Geotagged"
                    size="small"
                    color="success"
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      zIndex: 1,
                      fontWeight: 700,
                      fontSize: '10px',
                      height: '22px',
                      backdropFilter: 'blur(4px)',
                      backgroundColor: 'rgba(76, 175, 80, 0.9)'
                    }}
                  />
                )}
                <Box
                  sx={{
                    width: '100%',
                    height: 200,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    '&:hover img': {
                      transform: 'scale(1.05)'
                    }
                  }}
                  onClick={() => window.open(`${API_SERVER_URL}${photoPath}`, '_blank')}
                >
                  <img
                    src={`${API_SERVER_URL}${photoPath}`}
                    alt={`Photo ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease'
                    }}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-size="14"%3EImage%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </Box>
                <CardContent sx={{ py: 1.5, px: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Photo {index + 1}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Click to preview full size
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <ActionButton
          variant="primary"
          fullWidth
          startIcon={postingToGMB ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <CloudUpload />}
          onClick={handlePostToGMB}
          disabled={postingToGMB}
        >
          {postingToGMB ? 'Posting to GMB...' : '✅ Post to Google My Business'}
        </ActionButton>

        <ActionButton
          variant="secondary"
          fullWidth
          startIcon={<Close />}
          onClick={handleRejectPhotos}
          disabled={postingToGMB}
        >
          ❌ Not Good - Request Different Photos
        </ActionButton>
      </Stack>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{ color: '#666' }}
        >
          Back to Task
        </Button>
      </Box>
    </ContentContainer>
  );

  const activeGeotagPhoto = geotagDialogState.photoId
    ? photos.find((photo) => photo.id === geotagDialogState.photoId)
    : null;

  return (
    <>
      <Box>
        <HeaderContainer>
          <IconContainer>
            <PhotoCamera sx={{ fontSize: 40, color: 'white' }} />
          </IconContainer>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            {step === 'admin-review' ? 'Review Photos' : 'Upload Photos'}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {step === 'admin-review'
              ? 'Review and approve photos uploaded by admin'
              : 'Add high-quality photos to showcase your business and attract customers'
            }
          </Typography>
        </HeaderContainer>

        {step === 'upload' && renderPhotoUpload()}
        {step === 'success' && renderSuccess()}
        {step === 'admin-review' && renderAdminPhotoReview()}
      </Box>

      <GeotagDialog
        open={geotagDialogState.open}
        onClose={() => setGeotagDialogState({ open: false, photoId: null, defaults: null })}
        defaults={geotagDialogState.defaults || {}}
        initialMetadata={activeGeotagPhoto?.geotag || null}
        onApply={handleApplyGeotag}
        onGenerateDescription={handleGenerateGeotagDescription}
        generatingDescription={geotagGenerating}
      />
    </>
  );
};

export default PhotoUploadPopup;
